import React, { useState, useEffect, useRef } from "react";
import API from "../api/axiosConfig";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import ReCAPTCHA from "react-google-recaptcha";
import { MessageCircle, Mail, Phone, ShieldCheck, Loader2 } from "lucide-react";
import CountryPicker from "../components/CountryPicker";
import { getCleanPhone } from "../utils/phoneCountryCodes";
import { RECAPTCHA_SITE_KEY as SITE_KEY, storeSessionAndRedirect } from "../utils/authSession";
import { isNativeApp } from "../utils/platform";
import { nativeGoogleSignIn } from "../utils/googleAuth";

function WhatsAppLoginForm({ nav }) {
  const [step,             setStep]             = useState("phone"); // "phone" | "name" | "code"
  const [phone,            setPhone]            = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("+58");
  const [name,             setName]             = useState("");
  const [code,             setCode]             = useState("");
  const [sending,          setSending]          = useState(false);
  const [verifying,        setVerifying]        = useState(false);
  const recaptchaRef = useRef(null);

  const { clean: cleanPhone, expected } = getCleanPhone(phone, phoneCountryCode);

  const doSendOtp = async () => {
    const captchaToken = isNativeApp ? undefined : recaptchaRef.current?.getValue();
    if (!isNativeApp && !captchaToken) { toast.warning("Completá el CAPTCHA"); return; }
    setSending(true);
    try {
      const payload = { phone: cleanPhone, phoneCountryCode, captchaToken };
      if (step === "name") payload.name = name.trim();
      await API.post("/auth/whatsapp/send-otp", payload);
      toast.success("Te enviamos un código por WhatsApp 📲");
      setCode("");
      setStep("code");
    } catch (err) {
      if (err.response?.status === 428) {
        toast.info("No encontramos una cuenta con ese número — decinos tu nombre para crearla");
        setStep("name");
      } else {
        toast.error(err.response?.data?.message || "No se pudo enviar el código");
        recaptchaRef.current?.reset();
      }
    } finally { setSending(false); }
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    if (!cleanPhone || cleanPhone.length !== expected) {
      toast.warning(`El número debe tener exactamente ${expected} dígitos`);
      return;
    }
    await doSendOtp();
  };

  const handleNameSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.warning("Ingresá tu nombre");
    await doSendOtp();
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return toast.warning("El código debe tener 6 dígitos");
    setVerifying(true);
    try {
      const res = await API.post("/auth/whatsapp/verify-otp", { phone: cleanPhone, phoneCountryCode, code });
      const isNewUser = res.data?.data?.isNewUser ?? res.data?.isNewUser;
      toast.success(isNewUser ? "🎉 Cuenta creada" : "Bienvenido de nuevo 👋");
      await storeSessionAndRedirect(res, nav);
    } catch (err) {
      toast.error(err.response?.data?.message || "Código incorrecto");
    } finally { setVerifying(false); }
  };

  if (step === "code") {
    return (
      <form onSubmit={handleVerify} className="flex flex-col gap-4">
        <p className="text-sm text-gray-500 text-center">
          Ingresá el código de 6 dígitos que enviamos por WhatsApp al{" "}
          <span className="font-semibold text-gray-700">{phoneCountryCode} {cleanPhone}</span>
        </p>
        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          placeholder="000000"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          className="p-3 border border-gray-300 rounded-xl text-center text-2xl tracking-[0.5em] font-mono focus:ring-2 focus:ring-blue-400 outline-none"
          required
          autoFocus
        />
        <motion.button
          whileTap={{ scale: 0.97 }}
          type="submit"
          disabled={verifying}
          className="flex items-center justify-center gap-2 bg-supernova-500 text-white py-3 rounded-xl font-semibold hover:bg-supernova-700 transition-all duration-200 shadow-md disabled:opacity-60"
        >
          {verifying ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
          Verificar e ingresar
        </motion.button>
        <button
          type="button"
          onClick={() => setStep("phone")}
          className="text-xs text-gray-500 hover:underline"
        >
          ← ¿No te llegó? Volver a intentar
        </button>
      </form>
    );
  }

  if (step === "name") {
    return (
      <form onSubmit={handleNameSubmit} className="flex flex-col gap-4">
        <p className="text-sm text-gray-500 text-center">
          No encontramos una cuenta con el <span className="font-semibold text-gray-700">{phoneCountryCode} {cleanPhone}</span>.
          Decinos tu nombre para crear tu cuenta.
        </p>
        <input
          type="text"
          placeholder="Nombre"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none"
          required
          autoFocus
        />
        {!isNativeApp && (
          <div className="flex justify-center pt-1">
            <ReCAPTCHA ref={recaptchaRef} sitekey={SITE_KEY} />
          </div>
        )}
        <motion.button
          whileTap={{ scale: 0.97 }}
          type="submit"
          disabled={sending}
          className="flex items-center justify-center gap-2 bg-supernova-500 text-white py-3 rounded-xl font-semibold hover:bg-supernova-700 transition-all duration-200 shadow-md disabled:opacity-60"
        >
          {sending ? <Loader2 size={16} className="animate-spin" /> : <MessageCircle size={16} />}
          Crear cuenta y enviar código
        </motion.button>
        <button
          type="button"
          onClick={() => setStep("phone")}
          className="text-xs text-gray-500 hover:underline"
        >
          ← Cambiar número
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={handlePhoneSubmit} className="flex flex-col gap-4">
      <div>
        <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Número de WhatsApp</label>
        <div className="flex gap-2">
          <CountryPicker value={phoneCountryCode} onChange={(c) => { setPhoneCountryCode(c); setPhone(""); }} />
          <div className="relative flex-1">
            <Phone size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="tel"
              placeholder="Número de celular"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full pl-9 pr-3 py-3 border border-gray-300 rounded-xl outline-none transition focus:ring-2 focus:ring-blue-400"
              required
            />
          </div>
        </div>
      </div>

      {!isNativeApp && (
        <div className="flex justify-center pt-1">
          <ReCAPTCHA ref={recaptchaRef} sitekey={SITE_KEY} />
        </div>
      )}

      <motion.button
        whileTap={{ scale: 0.97 }}
        type="submit"
        disabled={sending}
        className="flex items-center justify-center gap-2 bg-supernova-500 text-white py-3 rounded-xl font-semibold hover:bg-supernova-700 transition-all duration-200 shadow-md disabled:opacity-60"
      >
        {sending ? <Loader2 size={16} className="animate-spin" /> : <MessageCircle size={16} />}
        Enviar código por WhatsApp
      </motion.button>
    </form>
  );
}

export default function Login() {
  const [mode, setMode] = useState("whatsapp"); // "whatsapp" | "traditional"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    try {
      const res = await API.post("/auth/login", { email, password });

      // nuevo backend: { success, data: { user, tokens: { accessToken, refreshToken } } }
      const payload = res.data?.data ?? res.data;
      const user    = payload?.user  ?? payload;
      const tokens  = payload?.tokens ?? {};

      localStorage.setItem("token",        tokens.accessToken  ?? "");
      localStorage.setItem("accessToken",  tokens.accessToken  ?? "");
      localStorage.setItem("refreshToken", tokens.refreshToken ?? "");
      localStorage.setItem("user",         JSON.stringify(user));

      if (user.role === "ryder") {
        try {
          const ryderRes = await API.get(`/ryders/user/${user.id}`);
          const ryder = ryderRes.data?.data?.ryder ?? ryderRes.data?.ryder ?? ryderRes.data;
          localStorage.setItem("ryderId", ryder?.id ?? ryder?._id ?? "");
        } catch {}
      }

      toast.success("Bienvenido 👋");

      if (user.role === "company")       nav("/company");
      else if (user.role === "admin")    nav("/admin");
      else if (user.role === "ryder")    nav("/ryder");
      else if (user.role === "delivery") nav("/delivery/dispatch");
      else nav("/");
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.msg || "Error al ingresar");
    }
  };

  const handleGoogleResponse = async (response) => {
    try {
      const res = await API.post("/auth/google", {
        credential: response.credential,
      });

      const payload = res.data?.data ?? res.data;
      const user    = payload?.user  ?? payload;
      const tokens  = payload?.tokens ?? {};

      localStorage.setItem("token",        tokens.accessToken  ?? "");
      localStorage.setItem("accessToken",  tokens.accessToken  ?? "");
      localStorage.setItem("refreshToken", tokens.refreshToken ?? "");
      localStorage.setItem("user",         JSON.stringify(user));

      if (user.role === "ryder") {
        try {
          const ryderRes = await API.get(`/ryders/user/${user.id}`);
          const ryder = ryderRes.data?.data?.ryder ?? ryderRes.data?.ryder ?? ryderRes.data;
          localStorage.setItem("ryderId", ryder?.id ?? ryder?._id ?? "");
        } catch {}
      }

      toast.success("Bienvenido 👋");

      if (user.role === "company")       nav("/company");
      else if (user.role === "admin")    nav("/admin");
      else if (user.role === "ryder")    nav("/ryder");
      else if (user.role === "delivery") nav("/delivery/dispatch");
      else nav("/");
    } catch (err) {
      toast.error("Error con Google. Intenta de nuevo.");
    }
  };

  const handleNativeGoogleSignIn = async () => {
    try {
      const idToken = await nativeGoogleSignIn();
      await handleGoogleResponse({ credential: idToken });
    } catch (err) {
      if (!/cancel/i.test(err?.message || "")) {
        toast.error("Error con Google. Intenta de nuevo.");
      }
    }
  };

  useEffect(() => {
    // En la app nativa el botón se maneja aparte, vía Google Sign-In nativo
    // (ver handleNativeGoogleSignIn) — el widget web de Google no funciona
    // embebido en el WebView de Capacitor.
    if (isNativeApp) return;

    // Si ya está cargado reutilizarlo
    if (window.google?.accounts) {
      const btn = document.getElementById("googleBtn");
      if (btn) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
        });
        window.google.accounts.id.renderButton(btn, {
          theme: "outline", size: "large", width: "100%", shape: "pill",
        });
      }
      return;
    }
    // Evitar duplicados
    if (document.querySelector('script[src="https://accounts.google.com/gsi/client"]')) return;
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (!window.google?.accounts) return;
      const btn = document.getElementById("googleBtn");
      if (!btn) return;
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
      });
      window.google.accounts.id.renderButton(btn, {
        theme: "outline", size: "large", width: "100%", shape: "pill",
      });
    };
    document.body.appendChild(script);
    // eslint-disable-next-line
  }, [mode]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-supernova-50 to-indigo-100 px-4">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white/80 backdrop-blur-md p-8 rounded-2xl shadow-xl w-full max-w-md"
      >
        <h2 className="text-3xl font-bold text-center text-supernova-600 mb-6">
          Inicia sesión
        </h2>

        {/* Selector de modo */}
        <div className="flex rounded-xl bg-gray-100 p-1 mb-6">
          <button
            type="button"
            onClick={() => setMode("whatsapp")}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition ${
              mode === "whatsapp" ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <MessageCircle size={15} /> WhatsApp rápido
          </button>
          <button
            type="button"
            onClick={() => setMode("traditional")}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition ${
              mode === "traditional" ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Mail size={15} /> Correo
          </button>
        </div>

        {mode === "whatsapp" ? (
          <WhatsAppLoginForm nav={nav} />
        ) : (
        <form onSubmit={submit} className="flex flex-col gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Correo electrónico
            </label>
            <input
              type="email"
              placeholder="tu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Contraseña
            </label>
            <input
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
              required
            />
          </div>

          <div className="text-right text-sm mt-2">
            <button
              type="button"
              onClick={() => nav("/forgot-password")}
              className="text-supernova-600 hover:underline font-medium"
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>

          <motion.button
            whileTap={{ scale: 0.97 }}
            type="submit"
            className="bg-supernova-500 text-white py-3 rounded-xl font-semibold hover:bg-supernova-700 transition-all duration-200 shadow-md"
          >
            Ingresar
          </motion.button>
        </form>
        )}

        {mode === "traditional" && (
          <>
            <div className="flex items-center my-6">
              <div className="flex-grow h-px bg-gray-300"></div>
              <span className="px-2 text-gray-500 text-sm font-medium">o</span>
              <div className="flex-grow h-px bg-gray-300"></div>
            </div>

            {isNativeApp ? (
              <button
                type="button"
                onClick={handleNativeGoogleSignIn}
                className="flex items-center justify-center gap-2 border border-gray-300 rounded-full py-2.5 font-semibold text-sm text-gray-700 hover:bg-gray-50 transition"
              >
                <svg width="18" height="18" viewBox="0 0 18 18">
                  <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
                  <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
                  <path fill="#FBBC05" d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.05l3.01-2.33z" />
                  <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
                </svg>
                Continuar con Google
              </button>
            ) : (
              <div id="googleBtn" className="flex justify-center"></div>
            )}
          </>
        )}

        <div className="mt-6 text-center text-sm text-gray-600">
          ¿No tienes cuenta?{" "}
          <a href="/register" className="text-supernova-600 hover:underline font-medium">
            Regístrate aquí
          </a>
        </div>

        <div className="mt-6 space-y-2">
          <a
            href="/register-ryder"
            className="inline-flex items-center gap-2 w-full justify-center bg-gray-900 hover:bg-gray-800 border border-gray-700 text-white font-semibold py-2.5 rounded-xl transition text-sm"
          >
            🛵 Quiero ser domiciliario
          </a>
          <a
            href="/register-business"
            className="inline-flex items-center gap-2 w-full justify-center bg-supernova-400 hover:bg-supernova-500 text-black font-semibold py-2.5 rounded-xl transition text-sm"
          >
            🏪 Registra tu comercio
          </a>
          <a
            href="/register-delivery"
            className="inline-flex items-center gap-2 w-full justify-center bg-indigo-100 hover:bg-indigo-200 text-indigo-700 font-semibold py-2.5 rounded-xl transition text-sm"
          >
            🚚 Tengo una empresa de domicilios
          </a>
        </div>
      </motion.div>
    </div>
  );
}
